/**
 * Track-claim display helpers + type aliases — /claim/track/:token (#1066).
 *
 * The contract types live in @regatta-replay/types (api.ts / entities.ts);
 * the aliases below preserve the names this app adopted while the API and
 * web halves were built in parallel against the frozen contract.
 */

import type {
  GetTrackClaimResponse,
  ClaimTrackResponse,
  TrackClaimEntryOption,
} from '@regatta-replay/types';

export type { TrackClaimRaceDetected } from '@regatta-replay/types';

export type TrackClaimResponse = GetTrackClaimResponse;
export type TrackClaimResult = ClaimTrackResponse;
export type TrackClaimEntry = TrackClaimEntryOption;
export type TrackClaimStatus = GetTrackClaimResponse['status'];
export type TrackClaimTrack = GetTrackClaimResponse['track'];
export type TrackClaimRegatta = NonNullable<GetTrackClaimResponse['match']['regatta']>;

/** "2025-06-15" (or ISO timestamp) → "Sun, Jun 15" */
export function formatTrackDate(dateStr: string): string {
  // Date-only strings parse as UTC midnight — format in UTC to avoid the
  // local-timezone off-by-one-day shift.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const d = new Date(dateOnly ? `${dateStr}T00:00:00Z` : dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(dateOnly ? { timeZone: 'UTC' } : {}),
  });
}

/** 15600 → "4h 20m"; 540 → "9m" */
export function formatTrackDuration(durationSec: number): string {
  const totalMinutes = Math.round(durationSec / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
